





// src/services/WebSocketService.js



import { Client } from "@stomp/stompjs";

class WebSocketService {
    constructor() {
        this.client = null;
        this.connected = false;
        this.subscriptions = new Map();
        this.vendorId = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;

        // Define your current topics - EASILY ADD MORE HERE IN FUTURE
        this.availableTopics = [
            {
                name: 'vendor',
                path: (vendorId) => `/topic/vendor/${vendorId}`,
                description: 'General vendor updates'
            },
            {
                name: 'vendor-orders',
                path: (vendorId) => `/topic/vendor-orders/${vendorId}`,
                description: 'All vendor orders'
            },
            {
                name: 'online-orders',
                path: (vendorId) => `/topic/online-orders/${vendorId}`,
                description: 'Online orders only'
            },
            {
                name: 'vendor-cart-updates',
                path: (vendorId) => `/topic/vendor-cart-updates/${vendorId}`,
                description: 'Cart updates'
            },
            {
                name: 'offline-orders',
                path: (vendorId) => `/topic/offline-orders/${vendorId}`,
                description: 'Offline orders'
            },
            // 👆 FUTURE TOPICS - Just add more here like:
            // {
            //     name: 'notifications',
            //     path: (vendorId) => `/topic/notifications/${vendorId}`,
            //     description: 'Notifications'
            // },
            // {
            //     name: 'payments',
            //     path: (vendorId) => `/topic/payments/${vendorId}`,
            //     description: 'Payment updates'
            // }
        ];

        // Store topic handlers
        this.topicHandlers = new Map();
    }

    /**
     * Connect to WebSocket and subscribe to all topics
     * @param {string} vendorId - Vendor ID from login
     */
    connect(vendorId) {
        if (!vendorId) {
            console.error("❌ No vendor ID provided for WebSocket connection");
            return;
        }

        this.vendorId = vendorId;

        // Don't reconnect if already connected with same vendor
        if (this.client && this.connected) {
            //console.log(`WebSocket already connected for vendor ${vendorId}`);
            return;
        }

        //console.log(`🟢 Connecting WebSocket for vendor: ${vendorId}`);

        // Create new STOMP client
        this.client = new Client({
            brokerURL: "wss://backend.maamaas.com/food/ws",
            // brokerURL: "ws://testing.maamaas.com/food/ws",
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            debug: (str) => {
                // Only log in development
                if (process.env.NODE_ENV !== 'production') {
                    //console.log("STOMP:", str);
                }
            },
            connectHeaders: {
                'vendor-id': vendorId
            }
        });

        // Handle successful connection
        this.client.onConnect = () => {
            //console.log("✅ WebSocket Connected Successfully");
            this.connected = true;
            this.reconnectAttempts = 0;

            // Subscribe to all available topics
            this.subscribeToAllTopics();

            // Store connection info
            localStorage.setItem('websocket_connected', 'true');
            localStorage.setItem('websocket_vendor', vendorId);

            //console.log(`📡 Subscribed to ${this.availableTopics.length} topics`);
        };

        // Handle STOMP errors
        this.client.onStompError = (frame) => {
            console.error("❌ STOMP Error:", frame.headers["message"]);
            console.error("Details:", frame.body);
        };

        // Handle WebSocket close
        this.client.onWebSocketClose = (event) => {
            //console.log("⚠️ WebSocket Closed:", event);
            this.connected = false;
            localStorage.setItem('websocket_connected', 'false');
        };

        // Handle WebSocket errors
        this.client.onWebSocketError = (event) => {
            console.error("🔴 WebSocket Error:", event);
        };

        // Activate connection
        this.client.activate();
    }

    /**
     * Subscribe to all configured topics
     */
    subscribeToAllTopics() {
        if (!this.vendorId) {
            console.error("❌ No vendor ID available for subscription");
            return;
        }

        // Subscribe to each topic in the availableTopics array
        this.availableTopics.forEach(topicConfig => {
            const topicPath = topicConfig.path(this.vendorId);

            this.subscribe(topicPath, (message) => {
                this.handleMessage(topicConfig.name, topicPath, message);
            });

            //console.log(`  ✅ Subscribed to: ${topicPath} (${topicConfig.description})`);
        });

        // Store subscribed topics in localStorage
        const subscribedPaths = this.availableTopics.map(t => t.path(this.vendorId));
        localStorage.setItem('subscribed_topics', JSON.stringify(subscribedPaths));
    }

    /**
     * Subscribe to a specific topic
     */
    subscribe(topic, handler) {
        if (!this.client || !this.connected) {
            console.warn(`⚠️ Cannot subscribe to ${topic}: WebSocket not connected`);
            return null;
        }

        try {
            const subscription = this.client.subscribe(topic, (message) => {
                try {
                    // Try to parse as JSON
                    let data;
                    try {
                        data = JSON.parse(message.body);
                    } catch {
                        data = message.body;
                    }

                    handler(data);

                } catch (error) {
                    console.error(`❌ Error handling message for ${topic}:`, error);
                }
            });

            // Store subscription
            this.subscriptions.set(topic, subscription);

            return subscription;

        } catch (error) {
            console.error(`❌ Error subscribing to ${topic}:`, error);
            return null;
        }
    }

    /**
     * Handle incoming messages
     */
    handleMessage(topicName, topicPath, message) {
        //console.log(`📦 Message received on ${topicName}:`, message);

        // Store in localStorage based on topic
        this.storeMessage(topicName, message);

        // Trigger custom event for components to listen
        window.dispatchEvent(new CustomEvent('websocket-message', {
            detail: {
                topic: topicName,
                topicPath: topicPath,
                data: message,
                timestamp: new Date().toISOString()
            }
        }));
    }

    /**
     * Store messages in localStorage
     */
    storeMessage(topicName, message) {
        try {
            // Create a key for this topic
            const storageKey = `websocket_${topicName}`;

            // Get existing messages
            let existing = [];
            const stored = localStorage.getItem(storageKey);

            if (stored) {
                try {
                    existing = JSON.parse(stored);
                    // Ensure it's an array
                    if (!Array.isArray(existing)) {
                        console.warn(`⚠️ ${storageKey} was not an array, resetting`);
                        existing = [];
                    }
                } catch (e) {
                    console.warn(`⚠️ Could not parse ${storageKey}, resetting`);
                    existing = [];
                }
            }

            // Add new message with timestamp
            const messageWithTime = {
                ...message,
                receivedAt: new Date().toISOString(),
                topic: topicName
            };

            // For 'vendor' topic, we might want to store only latest or merge differently
            if (topicName === 'vendor') {
                // Option 1: Store only the latest vendor update
                localStorage.setItem(`${storageKey}_latest`, JSON.stringify(messageWithTime));

                // Option 2: Keep last 10 vendor updates
                existing.unshift(messageWithTime);
                const toStore = existing.slice(0, 10);
                localStorage.setItem(storageKey, JSON.stringify(toStore));
            } else {
                // For other topics, keep last 20
                existing.unshift(messageWithTime);
                const toStore = existing.slice(0, 20);
                localStorage.setItem(storageKey, JSON.stringify(toStore));
            }

            // Also store latest message separately for quick access
            localStorage.setItem(`${storageKey}_latest`, JSON.stringify(messageWithTime));

        } catch (error) {
            console.error(`Error storing message for ${topicName}:`, error);
        }
    }

    /**
     * Get messages for a specific topic
     */
    getMessages(topicName) {
        try {
            const storageKey = `websocket_${topicName}`;
            return JSON.parse(localStorage.getItem(storageKey) || '[]');
        } catch (error) {
            console.error(`Error getting messages for ${topicName}:`, error);
            return [];
        }
    }

    /**
     * Get latest message for a specific topic
     */
    getLatestMessage(topicName) {
        try {
            const storageKey = `websocket_${topicName}_latest`;
            return JSON.parse(localStorage.getItem(storageKey) || 'null');
        } catch (error) {
            console.error(`Error getting latest message for ${topicName}:`, error);
            return null;
        }
    }

    /**
     * Clear messages for a specific topic
     */
    clearMessages(topicName) {
        const storageKey = `websocket_${topicName}`;
        localStorage.removeItem(storageKey);
        localStorage.removeItem(`${storageKey}_latest`);
    }

    /**
     * Add a new topic at runtime (for future use)
     */
    addTopic(topicName, pathFunction, description = '') {
        // Check if topic already exists
        const exists = this.availableTopics.some(t => t.name === topicName);
        if (exists) {
            console.warn(`⚠️ Topic ${topicName} already exists`);
            return false;
        }

        // Add new topic
        this.availableTopics.push({
            name: topicName,
            path: pathFunction,
            description
        });

        // If already connected, subscribe to this new topic
        if (this.connected && this.vendorId) {
            const topicPath = pathFunction(this.vendorId);
            this.subscribe(topicPath, (message) => {
                this.handleMessage(topicName, topicPath, message);
            });
            //console.log(`✅ Added and subscribed to new topic: ${topicName}`);
        }

        return true;
    }

    /**
     * Remove a topic
     */
    removeTopic(topicName) {
        const index = this.availableTopics.findIndex(t => t.name === topicName);
        if (index === -1) return false;

        // Unsubscribe if connected
        if (this.connected && this.vendorId) {
            const topicPath = this.availableTopics[index].path(this.vendorId);
            this.unsubscribe(topicPath);
        }

        // Remove from array
        this.availableTopics.splice(index, 1);
        //console.log(`✅ Removed topic: ${topicName}`);
        return true;
    }

    /**
     * Unsubscribe from a specific topic
     */
    unsubscribe(topicPath) {
        if (this.subscriptions.has(topicPath)) {
            const subscription = this.subscriptions.get(topicPath);
            subscription.unsubscribe();
            this.subscriptions.delete(topicPath);
            //console.log(`✅ Unsubscribed from ${topicPath}`);
        }
    }

    /**
     * Disconnect WebSocket (on logout)
     */
    disconnect() {
        //console.log("🔌 Disconnecting WebSocket for vendor:", this.vendorId);

        // Unsubscribe from all topics
        this.subscriptions.forEach((subscription, topicPath) => {
            try {
                subscription.unsubscribe();
                //console.log(`  ✅ Unsubscribed from ${topicPath}`);
            } catch (error) {
                console.error(`  ❌ Error unsubscribing from ${topicPath}:`, error);
            }
        });

        // Clear subscriptions
        this.subscriptions.clear();

        // Deactivate client
        if (this.client) {
            this.client.deactivate();
            this.client = null;
        }

        // Reset state
        this.connected = false;
        this.vendorId = null;
        this.reconnectAttempts = 0;

        // Clear WebSocket related localStorage
        localStorage.removeItem('websocket_connected');
        localStorage.removeItem('websocket_vendor');
        localStorage.removeItem('subscribed_topics');

        //console.log("✅ WebSocket disconnected successfully");
    }

    /**
     * Check if connected
     */
    isConnected() {
        return this.connected;
    }

    /**
     * Get list of all topics
     */
    getTopics() {
        return this.availableTopics.map(t => ({
            ...t,
            currentPath: this.vendorId ? t.path(this.vendorId) : null,
            isSubscribed: this.vendorId ? this.subscriptions.has(t.path(this.vendorId)) : false
        }));
    }
}

// Create singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;